/**
 * `clone` — the hand-written deep copy (SPEC.md §4.2, §4.6).
 *
 * Hand-written, not `structuredClone`: the search clones before every simulated action and
 * the perf contract is ≤ 50µs. Every mutable part of the state is copied; the only shared
 * reference is `catalog`, which is immutable printed card data.
 *
 * Whenever a field is added to `GameState`, it must be added here — the clone-independence
 * fuzz invariant (SPEC.md §9) is what catches a missed one.
 */
import { cloneAbilityInstance, cloneBagState } from './bag.ts';
import { cloneIr } from './abilities/ir.ts';
import type { Effect } from './abilities/ir.ts';
import type { Action, ChoiceRequest } from './choices.ts';
import type { CostModifier } from './costs.ts';
import { cloneModifier } from './modifiers.ts';
import type {
  AwaitedChoice,
  CardInstance,
  CardStack,
  Frame,
  GameState,
  PlayerBinding,
  PlayerState,
  ResultBinding,
  TargetBinding,
} from './state.ts';

export function clone(state: GameState): GameState {
  return {
    catalog: state.catalog,
    rules: { ...state.rules },
    instances: state.instances.map(cloneInstance),
    players: state.players.map(clonePlayer),
    startingPlayer: state.startingPlayer,
    activePlayer: state.activePlayer,
    turn: state.turn,
    phase: state.phase,
    rng: { counter: state.rng.counter },
    stack: state.stack.map(cloneFrame),
    bag: cloneBagState(state.bag),
    modifiers: state.modifiers.map(cloneModifier),
    stacks: state.stacks.map(cloneStack),
    costModifiers: state.costModifiers.map(cloneCostModifier),
    abilityUses: [...state.abilityUses],
    questedThisTurn: [...state.questedThisTurn],
    challengedThisTurn: [...state.challengedThisTurn],
    wasChallengedThisTurn: [...state.wasChallengedThisTurn],
    playedThisTurn: [...state.playedThisTurn],
    banishedInChallengeThisTurn: [...state.banishedInChallengeThisTurn],
    cardsPutUnderThisTurn: [...state.cardsPutUnderThisTurn],
    leftDiscardThisTurn: [...state.leftDiscardThisTurn],
    banishedThisTurn: [...state.banishedThisTurn],
    damagedThisTurn: [...state.damagedThisTurn],
    damageRemovedThisTurn: [...state.damageRemovedThisTurn],
    pending: cloneRequest(state.pending),
    events: [...state.events],
    result: state.result === null ? null : { ...state.result },
  };
}

function cloneInstance(instance: CardInstance): CardInstance {
  return {
    id: instance.id,
    owner: instance.owner,
    controller: instance.controller,
    defId: instance.defId,
    zone: instance.zone,
    exerted: instance.exerted,
    damage: instance.damage,
    drying: instance.drying,
    location: instance.location,
    knownTo: instance.knownTo,
  };
}

function clonePlayer(player: PlayerState): PlayerState {
  return {
    index: player.index,
    deck: [...player.deck],
    hand: [...player.hand],
    play: [...player.play],
    inkwell: [...player.inkwell],
    discard: [...player.discard],
    under: [...player.under],
    looking: [...player.looking],
    lore: player.lore,
    inksThisTurn: player.inksThisTurn,
    cardsInkedThisTurn: player.cardsInkedThisTurn,
    cardsDrawnThisTurn: player.cardsDrawnThisTurn,
    cardsPutIntoDiscardThisTurn: player.cardsPutIntoDiscardThisTurn,
    cardsDiscardedThisTurn: player.cardsDiscardedThisTurn,
    alteredHand: player.alteredHand,
  };
}

/** One in-play pile (`stacks.ts`): three arrays of instance ids and nothing else. */
function cloneStack(stack: CardStack): CardStack {
  return { top: stack.top, under: [...stack.under], shifted: [...stack.shifted] };
}

function cloneFrame(frame: Frame): Frame {
  switch (frame.kind) {
    case 'game':
      return { kind: 'game' };
    case 'mulligan':
      return { kind: 'mulligan', player: frame.player, selected: [...frame.selected] };
    case 'turn-start':
      return {
        kind: 'turn-start',
        step: frame.step,
        readyChoice:
          frame.readyChoice === null
            ? null
            : {
                eligible: [...frame.readyChoice.eligible],
                cap: frame.readyChoice.cap,
                chosen: [...frame.readyChoice.chosen],
              },
      };
    case 'main':
      return { kind: 'main' };
    case 'challenge':
      return {
        kind: 'challenge',
        challenger: frame.challenger,
        step: frame.step,
        target: frame.target,
      };
    case 'bag':
      return { kind: 'bag', holder: frame.holder };
    case 'resolve-ability':
      return {
        kind: 'resolve-ability',
        item: cloneAbilityInstance(frame.item),
        started: frame.started,
      };
    case 'effect':
      return {
        kind: 'effect',
        source: frame.source,
        controller: frame.controller,
        subject: frame.subject,
        eventSource: frame.eventSource,
        eventAmount: frame.eventAmount,
        eventDefender: frame.eventDefender,
        label: frame.label,
        bindings: frame.bindings.map(cloneBinding),
        resultBindings: frame.resultBindings.map(cloneResultBinding),
        playerBindings: frame.playerBindings.map(clonePlayerBinding),
        queue: cloneIr(frame.queue) as Effect[],
        awaiting: frame.awaiting === null ? null : cloneAwaited(frame.awaiting),
        // The in-flight `custom()` transcript — plain data, so the clone resumes the
        // generator by replaying it (SPEC.md §4.3).
        custom: frame.custom === null ? null : cloneIr(frame.custom),
      };
    case 'enter-exerted':
      return { kind: 'enter-exerted', instance: frame.instance };
    case 'turn-end':
      return { kind: 'turn-end', step: frame.step };
  }
}

function cloneBinding(binding: TargetBinding): TargetBinding {
  return { id: binding.id, instances: [...binding.instances] };
}

function cloneResultBinding(binding: ResultBinding): ResultBinding {
  return { id: binding.id, amount: binding.amount };
}

function clonePlayerBinding(binding: PlayerBinding): PlayerBinding {
  return { id: binding.id, player: binding.player };
}

function cloneAwaited(awaiting: AwaitedChoice): AwaitedChoice {
  switch (awaiting.kind) {
    case 'target':
      return {
        kind: 'target',
        spec: cloneIr(awaiting.spec),
        picked: [...awaiting.picked],
        then: cloneIr(awaiting.then),
      };
    case 'choose-player':
      return {
        kind: 'choose-player',
        spec: cloneIr(awaiting.spec),
        then: cloneIr(awaiting.then),
      };
    case 'distribute':
      return {
        kind: 'distribute',
        spec: cloneIr(awaiting.spec),
        ...(awaiting.as === undefined ? {} : { as: awaiting.as }),
        remaining: awaiting.remaining,
        spent: awaiting.spent,
        picked: [...awaiting.picked],
        pendingTarget: awaiting.pendingTarget,
      };
    case 'may':
      return {
        kind: 'may',
        then: cloneIr(awaiting.then),
        ...(awaiting.label === undefined ? {} : { label: awaiting.label }),
        ...(awaiting.chooser === undefined ? {} : { chooser: cloneIr(awaiting.chooser) }),
        ...(awaiting.otherwise === undefined ? {} : { otherwise: cloneIr(awaiting.otherwise) }),
      };
    case 'modal':
      return { kind: 'modal', modes: cloneIr(awaiting.modes) };
    case 'custom-choice':
      return { kind: 'custom-choice', spec: cloneIr(awaiting.spec) };
    case 'discard':
      return { kind: 'discard', player: awaiting.player, remaining: awaiting.remaining };
  }
}

/**
 * A payment modifier is plain data with one nested value (its selector), so one structural
 * copy is correct and cannot drop a field the record grows later.
 */
function cloneCostModifier(modifier: CostModifier): CostModifier {
  return cloneIr(modifier);
}

/**
 * Requests and their options are treated as immutable by the engine, but a clone must be
 * independent even of a caller that mutates them, so they are copied too.
 */
function cloneRequest(request: ChoiceRequest): ChoiceRequest {
  return {
    kind: request.kind,
    player: request.player,
    options: request.options.map((each) => ({ key: each.key, action: cloneAction(each.action) })),
  };
}

/**
 * An action is flat except for the ones that name a list of cards: a `play`'s announced
 * payment and an `activate`'s banish/exert cost. Public discard-card payment ids are cloned by
 * the same IR walker.
 */
export function cloneAction(action: Action): Action {
  if (action.kind === 'play' && action.payment !== undefined) {
    return { kind: 'play', defId: action.defId, payment: cloneIr(action.payment) };
  }
  if (action.kind === 'activate') {
    return {
      ...action,
      ...(action.banish === undefined ? {} : { banish: [...action.banish] }),
      ...(action.exert === undefined ? {} : { exert: [...action.exert] }),
    };
  }
  return { ...action };
}
